import React, { useState } from 'react'
import { groupBy, isEmpty } from 'lodash'
import { makeStyles } from '@mui/styles'
import PropTypes from 'prop-types'

import lang from '@/lang'

import Loader from '@/modules/base/components/loader/loader'

import { Title } from '@/modules/base/components/texts/index'
import MassImportAssignmentItem from './mass-import-assignment-item'
import MassImportRequiredWarning from './mass-import-required-warning'
import DOAccordion from '@/modules/base/do-accordion'

const useStyles = makeStyles(theme => ({
  controls: {
    marginTop: '20px !important',
    borderBottom: `solid 1px ${theme.borderColor} !important`,
    '& > div': {
      margin: '0 !important',
      borderBottom: 'none !important',
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 21,
    paddingBottom: 14,
    borderBottom: theme.border,
    '& > span': {
      flex: '1 1 50%',
      textAlign: 'left',
      fontWeight: 600,
    },
    '& .field': {
      marginLeft: 32,
    },
  },
  loader: {
    height: '100%',
    top: 85,
  },
  container: {
    padding: '32px 29.1px 22.5px 32.2px',
    borderRadius: '4px',
    border: theme.border,
    backgroundColor: '#fff',
  },
}))

const { massImport: texts } = lang

const MassImportAssignment = (props) => {
  const {
    importSchema = [],
    importData = { columns: {}, rows: [] },
    importType = '',
    assignmentData = {},
    onChange,
  } = props
  const { columns } = importData
  const usedColumns = Object.keys(assignmentData).reduce((result, key) => {
    if (!isEmpty(assignmentData[key])) {
      result[key] = columns[key]
    }
    return result
  }, [])
  const notUsedColumns = Object.keys(columns).reduce((result, key) => {
    if (!Object.prototype.hasOwnProperty.call(usedColumns, key)) {
      result[key] = columns[key]
    }
    return result
  }, {})

  const groupedImportSchema = groupBy(importSchema, 'category')

  const classes = useStyles()

  const [isLoading] = useState(false)

  const getAssignmentItemValue = (importItemKey) => {
    return Object.keys(assignmentData).reduce((result, key) => {
      if (assignmentData[key].key === importItemKey) {
        result = key
      }
      return result
    }, '-1')
  }

  // Required fields not yet assigned to a column, counted per accordion. Fields sharing the same
  // `requiredGroup` (e.g. customer import: last name OR company name) count as a single missing
  // required field until at least one of them is assigned.
  const getMissingRequiredCount = (categoryItems) => {
    const isAssigned = (key) => getAssignmentItemValue(key) !== '-1'
    const groupedRequired = groupBy(categoryItems.filter(({ requiredGroup }) => requiredGroup), 'requiredGroup')
    const missingGroups = Object.values(groupedRequired).filter((items) => !items.some(({ key }) => isAssigned(key))).length
    const missingSingle = categoryItems.filter(({ required, requiredGroup, key }) => required && !requiredGroup && !isAssigned(key)).length
    return missingGroups + missingSingle
  }

  const keylessControls = Object.keys(groupedImportSchema).filter((key) => key === 'null').map((key) => {
    return groupedImportSchema[key].map((importItem, i) =>
      <MassImportAssignmentItem key={`assignmentItem#${i}`} value={getAssignmentItemValue(importItem.key)} importItem={importItem} columns={notUsedColumns} usedColumns={usedColumns} onChange={onChange} importType={importType} />,
    )
  })

  const controls = Object.keys(groupedImportSchema).filter((key) => key !== 'null').map(key => {
    const categoryItems = groupedImportSchema[key].map((importItem, i) =>
      <MassImportAssignmentItem key={`assignmentItem#${i}`} value={getAssignmentItemValue(importItem.key)} importItem={importItem} columns={notUsedColumns} usedColumns={usedColumns} onChange={onChange} importType={importType} />,
    )
    return (
      <DOAccordion
        key={key}
        title={lang.massImport2.importDefs[importType]?.categories[key] || key}
        titleAdornment={<MassImportRequiredWarning count={getMissingRequiredCount(groupedImportSchema[key])} />}
        content={categoryItems} />
    )
  })

  return (
    <div className={classes.container}>
      <Loader text={texts.validation} visible={isLoading} customClass={classes.loader} />
      <Title>{texts.assignment}</Title>
      <span className={classes.header}>
        <span>{texts.columnInFile}</span>
        <span className='field'>{texts.fieldInData}</span>
      </span>
      <div>
        {keylessControls}
      </div>
      <div className={classes.controls}>
        {controls}
      </div>
    </div>
  )
}

MassImportAssignment.propTypes = {
  importType: PropTypes.string,
  importSchema: PropTypes.array,
  importData: PropTypes.object,
  assignmentData: PropTypes.object,
}

export default MassImportAssignment
